import axios from 'axios'
import router from '../../router'
import { jwtDecode } from 'jwt-decode'

const apiBaseUrl = process.env.VUE_APP_API_BASE_URL;

export const PASSWORD_EXPIRED_CODE = 'PASSWORD_EXPIRED'

// Persist a freshly issued access token and hydrate the session from it.
async function applyAuthToken({ commit, dispatch }, token) {
  const decodedToken = jwtDecode(token)
  localStorage.setItem('token', token)
  localStorage.setItem('userID', decodedToken.userID)
  localStorage.setItem('username', decodedToken.username)
  localStorage.setItem('userFullname', decodedToken.userFirstname + ' ' + decodedToken.userLastname)
  localStorage.setItem('userTitle', decodedToken.userTitle)
  localStorage.setItem('companyID', decodedToken.companyID)
  localStorage.setItem('canViewEvoAppLeads', decodedToken.canViewEvoAppLeads ? 'true' : 'false')

  commit('setToken', token)
  commit('setUserID', decodedToken.userID)
  commit('setUsername', decodedToken.username)
  commit('setUserFullname', decodedToken.userFirstname + ' ' + decodedToken.userLastname)
  commit('setUserTitle', decodedToken.userTitle)
  commit('setCompanyID', decodedToken.companyID)
  commit('setCanViewEvoAppLeads', decodedToken.canViewEvoAppLeads === true)
  commit('setPasswordChangeToken', null)

  await dispatch('loadPermissions')
}

const actions = {
  // Resolves to { passwordExpired: true } when the backend refuses the login
  // because the password is older than the rotation period; the caller should
  // send the user to the forced change-password screen. Otherwise resolves to
  // { passwordExpired: false, passwordExpiresAt } and the session is ready.
  async login({ commit, dispatch }, authData) {
    try {
      const response = await axios.post(`${apiBaseUrl}/csms/login`, authData)
      await applyAuthToken({ commit, dispatch }, response.data.token)
      return { passwordExpired: false, passwordExpiresAt: response.data.password_expires_at || null }
    } catch (error) {
      const data = error.response?.data
      if (error.response?.status === 403 && data?.code === PASSWORD_EXPIRED_CODE && data?.password_change_token) {
        commit('setPasswordChangeToken', data.password_change_token)
        return { passwordExpired: true }
      }
      console.error('Login failed:', error)
      throw error
    }
  },

  // Completes the forced rotation: exchanges the scoped token plus the new
  // password for a regular session.
  async changeExpiredPassword({ commit, dispatch, state }, password) {
    const token = state.passwordChangeToken
    if (!token) {
      throw new Error('missing password change token')
    }
    const response = await axios.post(`${apiBaseUrl}/csms/change-expired-password`, { token, password })
    await applyAuthToken({ commit, dispatch }, response.data.token)
    return { passwordExpiresAt: response.data.password_expires_at || null }
  },

  async loadPermissions({ commit, state }) {
    try {
      const { data } = await axios.get(`${apiBaseUrl}/csms/user-data/permissions`, {
        headers: { Authorization: `Bearer ${state.token}` }
      })
      commit('SET_PERMISSIONS', data.permissions ?? null)
      commit('SET_PERMISSIONS_LOADED', true)
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        commit('clearAuthData')
        localStorage.clear()
        router.push('/auth/login')
      } else {
        commit('SET_PERMISSIONS', null)
        commit('SET_PERMISSIONS_LOADED', false)
      }
    }
  },

  logout({ commit }) {
    localStorage.clear()
    commit('clearAuthData')
    router.push('/auth/login')
  }
}

export default actions
